"use client";

import React, { useState, useEffect, useRef } from "react";
import { Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
} = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_front_home_services.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_front_home_services.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_front_home_services.png-wvc-sizes",
  alt: "Premium co-op postcard front design for home services",
  title: "Home Services Showcase",
  description: "A high-end co-op design featuring local HVAC, landscaping, and roofing experts."
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_back_tracking.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_back_tracking.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_back_tracking.png-wvc-sizes",
  alt: "Postcard back design with QR code tracking",
  title: "QR Tracking Layout",
  description: "The reverse side of our mailers optimized for conversion with unique QR codes."
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_health_wellness.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_health_wellness.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_health_wellness.png-wvc-sizes",
  alt: "Health and wellness business ad placement",
  title: "Health & Wellness",
  description: "Curated ad spots for local dentists, chiropractors, and boutique fitness studios."
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_food_beverage.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_food_beverage.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_food_beverage.png-wvc-sizes",
  alt: "Food and beverage postcard design",
  title: "Culinary Excellence",
  description: "Highlighting the best local dining and specialty food shops in Johns Creek."
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_luxury_real_estate.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_luxury_real_estate.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_luxury_real_estate.png-wvc-sizes",
  alt: "Luxury real estate marketing postcard",
  title: "Real Estate Prestige",
  description: "Premium cardstock designs tailored for high-income household delivery."
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_professional_services.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_professional_services.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_professional_services.png-wvc-sizes",
  alt: "Professional services ad layout",
  title: "Professional Services",
  description: "Clean, authoritative layouts for legal, financial, and insurance partners."
}, {
  id: 7,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_lifestyle_atlanta.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_lifestyle_atlanta.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_lifestyle_atlanta.png-wvc-sizes",
  alt: "Lifestyle imagery of Metro Atlanta",
  title: "Community Focused",
  description: "Designs that resonate with the local culture of Metro Atlanta neighborhoods."
}, {
  id: 8,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_eddm_delivery.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_eddm_delivery.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_eddm_delivery.png-wvc-sizes",
  alt: "USPS EDDM compliant postcard layout",
  title: "EDDM Optimization",
  description: "Perfectly formatted for USPS Every Door Direct Mail standards and delivery."
}, {
  id: 9,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_custom_design.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_custom_design.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_custom_design.png-wvc-sizes",
  alt: "Custom ad design for local business",
  title: "Bespoke Ad Design",
  description: "Our in-house team creates custom ads that align with your brand identity."
}, {
  id: 10,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_seasonal_campaign.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_seasonal_campaign.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/peachtree_postcard_seasonal_campaign.png-wvc-sizes",
  alt: "Seasonal marketing campaign postcard",
  title: "Seasonal Campaigns",
  description: "Timely mailers designed to capture peak consumer interest throughout the year."
}];
export default function GeorgiaGallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [selectedImage, setSelectedImage] = useState<typeof galleryImages[0] | null>(null);
  return <section data-section-id="241856" id="visual-gallery" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-secondary mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Our Design Portfolio
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg font-default">
            Explore our premium co-op postcard designs that connect trusted local businesses with high-income households across Metro Atlanta.
          </p>
        </div>

        <div ref={ref} className={`columns-1 gap-4 sm:columns-2 md:columns-3 lg:columns-4 space-y-4 transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {galleryImages.map((image, index) => <div key={image.id} data-index={index} className="group relative break-inside-avoid overflow-hidden rounded-sm bg-muted cursor-pointer shadow-md border border-border/50" onClick={() => setSelectedImage(image)}>
              <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-secondary/80 backdrop-blur-sm opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex flex-col items-center justify-center p-4 text-center">
                <Maximize2 className="text-primary mb-2 size-8 opacity-90" />
                <span className="font-default text-white font-semibold text-lg">
                  View Case Study
                </span>
                <span className="font-default text-white/80 text-sm mt-1">
                  {image.title}
                </span>
              </div>
            </div>)}
        </div>
      </div>

      <Dialog open={!!selectedImage} onOpenChange={open => !open && setSelectedImage(null)}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden bg-background border-border shadow-2xl">
          {selectedImage && <div className="flex flex-col">
              <div className="relative w-full bg-muted flex items-center justify-center min-h-[300px] max-h-[70vh]">
                <img src={selectedImage.src} data-wvc-srcset={selectedImage.srcset} data-wvc-sizes={selectedImage.sizes} alt={selectedImage.alt} className="w-full h-full object-contain max-h-[70vh]" />
              </div>
              <DialogHeader className="p-6 bg-card">
                <DialogTitle className="font-default text-secondary text-2xl font-bold">{selectedImage.title}</DialogTitle>
                <DialogDescription className="text-muted-foreground text-base mt-2 font-default">
                  {selectedImage.description}
                </DialogDescription>
              </DialogHeader>
            </div>}
        </DialogContent>
      </Dialog>
    </section>;
}